import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import TraditionTag from "@/components/TraditionTag";
import { ytThumbUrl } from "@/lib/youtube";
import { formatDuration } from "@/lib/catalog";

// A "card" in the music grid. If the song is a variant group, footer shows
// "3 versions" so the user knows the detail page has more.
export default function SongCard({ card, index = 0 }) {
  const rep = card.representative;
  const ytId = rep?.youtube_video_id;
  const cover = ytId ? ytThumbUrl(ytId, "hqdefault") : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.85,
        delay: Math.min(index * 0.05, 0.3),
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Link to={`/music/${card.slug}`} className="block group">
        <div className="card-shrine rounded-sm overflow-hidden">
          <div className="relative aspect-square overflow-hidden bg-parchment-deep">
            {cover ? (
              <>
                <img
                  src={cover}
                  alt={card.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-slower ease-breath group-hover:scale-[1.05]"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-shrine-shadow/60 via-transparent to-transparent" />
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-parchment via-parchment-deep to-dawn-rose/40">
                <span className="font-display text-4xl text-dharma-gold-deep/30">
                  ॐ
                </span>
              </div>
            )}
            <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-cream/90 flex items-center justify-center backdrop-blur-sm border border-dharma-gold/30 group-hover:bg-dharma-gold transition-colors duration-700 ease-breath">
              <Play
                size={14}
                className="text-dharma-gold-deep group-hover:text-cream transition-colors duration-700 ease-breath ml-0.5"
                fill="currentColor"
              />
            </div>
          </div>

          <div className="p-4 md:p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-base md:text-lg text-ink leading-snug group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath">
                {card.title}
              </h3>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <TraditionTag tradition={card.tradition} />
              {card.is_group ? (
                <span className="font-body text-[0.65rem] tracking-widest2 uppercase text-ink-mute">
                  {card.members.length} versions
                </span>
              ) : (
                rep?.duration_seconds && (
                  <span className="font-body text-[0.7rem] text-ink-mute">
                    {formatDuration(rep.duration_seconds)}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
